import type { ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';

import type { AnalyticsUser } from './types';
import { AnalyticsRoot } from './statsig-adapter';
import { useConsent } from '@/consent';
import { isAnalyticsActive } from '@/consent/store';

const ANONYMOUS_USER_ID = 'anonymous';

/**
 * Pure mapper from the Clerk user id + app locale to the vendor-neutral user.
 * Deliberately carries NO PII (no email, names, or Clerk publicMetadata) — only
 * the identity and the i18n locale. The `custom` channel stays open so future
 * non-PII targeting fields are a one-line addition here. Pure → unit-testable
 * without Clerk.
 */
export function buildAnalyticsUser(input: {
	userId: string | null | undefined;
	locale: string;
}): AnalyticsUser {
	const { userId, locale } = input;
	return { id: userId || ANONYMOUS_USER_ID, custom: { locale } };
}

/**
 * Mounts the Statsig client ONLY once the user has granted the analytics consent
 * category. While dormant, no client is constructed — no tracking cookies/storage,
 * autocapture, or session replay occur. The plugin set is fixed at first mount;
 * later category changes reload the page (Task 5) to re-init with new plugins.
 *
 * `AnalyticsRoot` is rendered as a SIBLING of `children`, not a wrapper: a wrapper
 * would change the element type at this slot (Fragment ↔ AnalyticsRoot) when
 * consent flips, forcing React to unmount/remount the entire app subtree
 * (router + Pixi editors, losing transient state). As a sibling, `children` keep
 * a stable position and are never remounted on consent change. Nothing currently
 * consumes the Statsig React context; the imperative facade, autocapture,
 * session replay, and events all operate at the client level. Feature gates
 * are not consent-gated and do not come from here: they are read from the
 * backend (see FeatureGateSync), whatever the user chose.
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
    const { userId } = useAuth();
    const { i18n } = useTranslation();
    const { consent } = useConsent();

    return (
        <>
            {isAnalyticsActive(consent) && (
                <AnalyticsRoot
                    user={buildAnalyticsUser({ userId, locale: i18n.language })}
                    sessionReplay={consent.sessionReplay}
                />
            )}
            {children}
        </>
    );
}
